import * as process from 'process';
import {
  createElement,
  Fragment,
  isValidElement as isReactElement,
} from 'react';
import { getStorageItem, setStorageItem } from '@snapp/client-utils';
import storageKeys from '../service/storageKeys';

type Language = 'fa' | 'en' | 'ar' | 'ku';
type Direction = 'rtl' | 'ltr';
type Locale = 'fa-IR' | 'ar-IQ' | 'ku-IQ' | 'en-GB';

type i18nConfig = {
  locale: Locale;
  language: Language;
  direction: Direction;
};

const PRIMARY_LOCALE: Locale = process.env.REACT_APP_PRIMARY_LOCALE as Locale;
const SECONDARY_LOCALE: Locale = process.env
  .REACT_APP_SECONDARY_LOCALE as Locale;

const localeConfig: { [key: string]: i18nConfig } = {
  [PRIMARY_LOCALE]: {
    locale: PRIMARY_LOCALE,
    language: process.env.REACT_APP_PRIMARY_LANGUAGE as Language,
    direction: process.env.REACT_APP_PRIMARY_DIRECTION as Direction,
  },
  [SECONDARY_LOCALE]: {
    locale: SECONDARY_LOCALE,
    language: process.env.REACT_APP_SECONDARY_LANGUAGE as Language,
    direction: process.env.REACT_APP_SECONDARY_DIRECTION as Direction,
  },
};

let currentLocale: Locale = getLocaleFromStorage();
let currentLocaleKeys: { [key: string]: string } = {};

async function setup() {
  switch (currentLocale) {
    case PRIMARY_LOCALE: {
      currentLocaleKeys = (
        await import(
          `@/brands/${process.env.REACT_APP_BRAND}/translations/${PRIMARY_LOCALE}`
        )
      ).default;
      break;
    }
    case SECONDARY_LOCALE: {
      currentLocaleKeys = (
        await import(
          `@/brands/${process.env.REACT_APP_BRAND}/translations/${SECONDARY_LOCALE}`
        )
      ).default;
      break;
    }
    default: {
      currentLocaleKeys = (
        await import(
          `@/brands/${process.env.REACT_APP_BRAND}/translations/${PRIMARY_LOCALE}`
        )
      ).default;
    }
  }
}

function getCurrentLocale(): i18nConfig {
  return {
    ...localeConfig[currentLocale],
  };
}

function setCurrentLocale(locale: Locale): void {
  if (locale !== currentLocale) {
    currentLocale = locale;
    setLocaleToStorage(locale || PRIMARY_LOCALE);
  }
}

function getLocaleFromStorage(): Locale {
  const storageItem = getStorageItem(storageKeys.locale) as Locale;
  if (!storageItem) {
    setLocaleToStorage(PRIMARY_LOCALE);
  }
  return storageItem || PRIMARY_LOCALE;
}

function setLocaleToStorage(locale: Locale): void {
  setStorageItem(storageKeys.locale, locale);
}

function getTranslationByKey(key: string): string | undefined {
  /**
   * primaryTranslations[key] is fallback
   */
  return currentLocaleKeys[key];
}

function translate(key: string, replace?): Array<any> | string {
  if (replace) {
    let valueToReturn = [getTranslationByKey(key)];
    let includesReactComponent = false;

    const replacer = (array, searchValue, replaceValue) => {
      for (let i = 0; i < array.length; i += 1) {
        if (typeof array[i] !== 'string') {
          // eslint-disable-next-line no-continue
          continue;
        }

        const indexOf = array[i].indexOf(searchValue);
        if (indexOf !== -1) {
          const [before, after] = array[i].split(searchValue);

          array.splice(i, 1, before, replaceValue, after);
          return array;
        }
      }
      return array;
    };

    Object.keys(replace).forEach(placeholder => {
      const searchValue = `{{${placeholder}}}`;
      const replaceValue = replace[placeholder];

      if (isReactElement(replaceValue)) {
        includesReactComponent = true;
      }

      valueToReturn = replacer(valueToReturn, searchValue, replaceValue);
    });

    if (includesReactComponent) {
      return valueToReturn.map((value, index) =>
        createElement(Fragment, { key: index }, value),
      );
    }

    return valueToReturn.join('');
  }

  return getTranslationByKey(key);
}

export { setup, getCurrentLocale, setCurrentLocale };

export default translate;
